'use client'

import { cx } from 'cva'
import type { FC } from 'react'
import { useId } from 'react'
import { FontAwesomeIcon } from './FontAwesomeIcon'
import InputError from './InputError'

type FormInputCheckboxProps = {
	name: string
	option: string
	defaultChecked?: boolean
}

export type FormInputCheckboxesProps = {
	name: string
	label: string
	helpText?: string
	required?: boolean
	options: string[]
	defaultValue?: string[]
	error?: string
}

const FormInputCheckbox: FC<FormInputCheckboxProps> = ({ name, option, defaultChecked }) => {
	const inputId = useId()
	return (
		<div className="flex items-center">
			<div className="relative flex items-center justify-start text-blue-shade-40">
				<input
					className="peer flex h-6 w-6 opacity-0"
					id={inputId}
					type="checkbox"
					name={name}
					value={option}
					defaultChecked={defaultChecked}
				/>
				<div
					className={cx([
						'pointer-events-none absolute top-0 left-0 h-6 w-6 flex items-center justify-center rounded-xs border-(length:--stroke-sm) border-grey-20 text-white',
						'peer-checked:border-blue peer-checked:bg-blue not-peer-checked:[&>*]:hidden',
					])}
				>
					<FontAwesomeIcon icon={['fas', 'check']} size="xs" />
				</div>
			</div>
			<label className="ml-2 text-body-sm text-blue-shade-40" htmlFor={inputId}>
				{option}
			</label>
		</div>
	)
}

/**
 * Renders a list of checkboxes.
 */
export const FormInputCheckboxes: FC<FormInputCheckboxesProps> = ({
	name,
	label,
	helpText,
	options,
	defaultValue,
	error,
}) => {
	return (
		<div>
			<fieldset>
				<legend className="text-h4">{label}</legend>
				{helpText && <p className="text-body-sm text-blue-shade-40">{helpText}</p>}
				<div className="mt-6 flex flex-col gap-2">
					{options.map((option) => (
						<FormInputCheckbox
							key={option}
							name={name}
							option={option}
							defaultChecked={defaultValue?.includes(option)}
						/>
					))}
				</div>
			</fieldset>
			<div className="mt-2">
				<InputError error={error} />
			</div>
		</div>
	)
}
